import {
  GOOGLE_CLIENT_ID,
  GOOGLE_API_KEY,
  GOOGLE_DRIVE_SCOPE,
  GOOGLE_DRIVE_FOLDER_NAME,
  GOOGLE_DRIVE_MIME_JSON,
  GOOGLE_DRIVE_MIME_FOLDER,
} from '../config/googleDrive';

export interface DriveFile {
  id: string;
  name: string;
  modifiedTime: string;
  size?: string;
}

export type DriveResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: string };

const TOKEN_STORAGE_KEY = 'google_drive_token';
const TOKEN_EXPIRY_KEY = 'google_drive_token_expiry';

function getStoredToken(): string | null {
  const token = sessionStorage.getItem(TOKEN_STORAGE_KEY);
  const expiry = sessionStorage.getItem(TOKEN_EXPIRY_KEY);
  if (!token || !expiry) return null;
  if (Date.now() > parseInt(expiry, 10)) {
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
    return null;
  }
  return token;
}

function setStoredToken(token: string, expiresIn: number): void {
  sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
  sessionStorage.setItem(
    TOKEN_EXPIRY_KEY,
    String(Date.now() + expiresIn * 1000)
  );
}

function clearStoredToken(): void {
  sessionStorage.removeItem(TOKEN_STORAGE_KEY);
  sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = Array.from(document.querySelectorAll('script')).find(
      (s) => s.src === src
    );
    if (existing) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`No se pudo cargar ${src}`));
    document.head.appendChild(script);
  });
}

async function ensureGapiLoaded(): Promise<void> {
  await loadScript('https://apis.google.com/js/api.js');
  return new Promise((resolve) => {
    window.gapi!.load('client', async () => {
      await window.gapi!.client.init({
        apiKey: GOOGLE_API_KEY,
        discoveryDocs: [
          'https://www.googleapis.com/discovery/v1/apis/drive/v3/rest',
        ],
      });
      resolve();
    });
  });
}

async function ensureGisLoaded(): Promise<void> {
  await loadScript('https://accounts.google.com/gsi/client');
}

export async function requestAccessToken(): Promise<DriveResult<string>> {
  const storedToken = getStoredToken();
  if (storedToken) {
    try {
      await ensureGapiLoaded();
      window.gapi!.client.setToken({ access_token: storedToken });
      return { ok: true, data: storedToken };
    } catch {
      clearStoredToken();
    }
  }

  try {
    await Promise.all([ensureGapiLoaded(), ensureGisLoaded()]);

    return new Promise((resolve) => {
      const tokenClient = window.google!.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: GOOGLE_DRIVE_SCOPE,
        callback: (response) => {
          if (response.error) {
            resolve({
              ok: false,
              error: response.error_description ?? 'Error de autenticación',
            });
            return;
          }
          const expiresIn = response.expires_in ?? 3600;
          setStoredToken(response.access_token, expiresIn);
          window.gapi!.client.setToken({
            access_token: response.access_token,
          });
          resolve({ ok: true, data: response.access_token });
        },
        error_callback: (error) => {
          resolve({
            ok: false,
            error: error.message ?? 'Error de autenticación',
          });
        },
      });
      tokenClient.requestAccessToken({ prompt: '' });
    });
  } catch (err) {
    return {
      ok: false,
      error:
        err instanceof Error ? err.message : 'Error al iniciar autenticación',
    };
  }
}

export function getAccessToken(): string | null {
  return getStoredToken();
}

export function revokeToken(): void {
  const token = getStoredToken();
  if (token) {
    window.google?.accounts.oauth2.revoke(token);
    window.gapi?.client.setToken(null);
    clearStoredToken();
  }
}

async function findOrCreateFolder(): Promise<DriveResult<string>> {
  try {
    const listResponse = await window.gapi!.client.drive.files.list({
      q: `mimeType='${GOOGLE_DRIVE_MIME_FOLDER}' and name='${GOOGLE_DRIVE_FOLDER_NAME}' and trashed=false`,
      fields: 'files(id, name)',
      spaces: 'drive',
    });

    const folders = listResponse.result.files;
    if (folders.length > 0) {
      return { ok: true, data: folders[0].id };
    }

    const createResponse = await window.gapi!.client.drive.files.create({
      resource: {
        name: GOOGLE_DRIVE_FOLDER_NAME,
        mimeType: GOOGLE_DRIVE_MIME_FOLDER,
      },
      fields: 'id',
    });

    return { ok: true, data: createResponse.result.id };
  } catch (err) {
    return {
      ok: false,
      error:
        err instanceof Error ? err.message : 'Error al buscar/crear la carpeta',
    };
  }
}

export async function uploadFile(
  jsonContent: string,
  filename: string
): Promise<DriveResult<{ id: string; name: string }>> {
  const token = getStoredToken();
  if (!token) {
    return { ok: false, error: 'No autenticado' };
  }

  try {
    const folderResult = await findOrCreateFolder();
    if (!folderResult.ok) {
      return folderResult;
    }

    const metadata = {
      name: filename,
      mimeType: GOOGLE_DRIVE_MIME_JSON,
      parents: [folderResult.data],
    };

    const boundary = '-------budgeter_boundary';
    const body = [
      `--${boundary}`,
      'Content-Type: application/json; charset=UTF-8',
      '',
      JSON.stringify(metadata),
      `--${boundary}`,
      `Content-Type: ${GOOGLE_DRIVE_MIME_JSON}`,
      '',
      jsonContent,
      `--${boundary}--`,
    ].join('\r\n');

    const response = await fetch(
      'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name',
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': `multipart/related; boundary=${boundary}`,
        },
        body,
      }
    );

    if (!response.ok) {
      const text = await response.text();
      return { ok: false, error: `Error al subir: ${text}` };
    }

    const result = (await response.json()) as { id: string; name: string };
    return { ok: true, data: { id: result.id, name: result.name } };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : 'Error al subir el archivo',
    };
  }
}

export async function listFiles(): Promise<DriveResult<DriveFile[]>> {
  if (!getStoredToken()) {
    return { ok: false, error: 'No autenticado' };
  }

  try {
    const folderResult = await findOrCreateFolder();
    if (!folderResult.ok) {
      return folderResult;
    }

    const response = await window.gapi!.client.drive.files.list({
      q: `'${folderResult.data}' in parents and mimeType='${GOOGLE_DRIVE_MIME_JSON}' and trashed=false`,
      fields: 'files(id, name, modifiedTime, size)',
      spaces: 'drive',
      orderBy: 'modifiedTime desc',
      pageSize: 50,
    });

    return { ok: true, data: response.result.files };
  } catch (err) {
    return {
      ok: false,
      error:
        err instanceof Error ? err.message : 'Error al listar los archivos',
    };
  }
}

export async function downloadFile(
  fileId: string
): Promise<DriveResult<string>> {
  const token = getStoredToken();
  if (!token) {
    return { ok: false, error: 'No autenticado' };
  }

  try {
    const response = await fetch(
      `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(fileId)}?alt=media`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      return { ok: false, error: 'Error al descargar el archivo' };
    }

    const text = await response.text();
    return { ok: true, data: text };
  } catch (err) {
    return {
      ok: false,
      error:
        err instanceof Error ? err.message : 'Error al descargar el archivo',
    };
  }
}
